/**
 * NodeEditPopup — modal chrome for editing a node's data.
 *
 * No card/frame — just a dimmed backdrop with the tabs and fields floating
 * directly on it, and a small close button floating just outside the
 * content's top-right corner. Changes are never discarded here, so the
 * close button is a tick (✓) — same icon and style as the EditToolbar's
 * save button — rather than a cross. The actual editable content is
 * mounted into the body container handed back to the caller — normally a
 * NodeVisualizer instance rendering its tabbed (Node/Ingredient/
 * Transformació) editable view.
 */
export class NodeEditPopup {
  private modalElement: HTMLElement | null = null;
  private bodyContainer: HTMLElement | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  private onCloseCallback: (() => void) | null = null;

  /**
   * Open the popup and return the body container for the caller to render
   * editable content into.
   */
  open(onClose: () => void): HTMLElement {
    this.onCloseCallback = onClose;

    this.modalElement = document.createElement('div');
    // items-start + a fixed top padding (rather than items-center) — the
    // popup's height varies with the active tab (Node/Ingredient/
    // Transformació have different field counts), and centering it would
    // shift the whole popup — tabs included — up and down as you switch
    // between them. Anchoring to a fixed distance from the top keeps the
    // tab bar itself perfectly still; only the content below it grows/
    // shrinks.
    this.modalElement.className =
      'fixed inset-0 bg-black/50 flex items-start justify-center z-50';
    this.modalElement.id = 'node-edit-popup';

    // Wrapper is the positioning root for the close button — kept separate
    // from `content` (which scrolls) so the button, floating just outside
    // content's bounds, is never clipped by content's own overflow.
    //
    // The top offset lives here as the wrapper's own margin-top rather
    // than as padding-top on the fixed/inset-0 flex container above —
    // on iPhone Safari, padding-top on a `position: fixed; inset: 0`
    // flex container was being dropped entirely (reproduces only on a
    // real device, not desktop devtools' responsive emulation), leaving
    // the popup flush against the very top of the screen. margin-top on
    // the flex item itself avoids that combination.
    // mt-32 (8rem) on larger screens; a smaller mt-8 on narrow/mobile
    // viewports, where 8rem of solid dimmed backdrop above the popup eats
    // a disproportionate chunk of the much shorter screen.
    const wrapper = document.createElement('div');
    wrapper.className = 'relative max-w-lg w-full mx-4 mt-8 sm:mt-32';

    const content = document.createElement('div');
    // Height budget matches the modal's own top offset at each breakpoint,
    // plus an equal gap reserved at the bottom.
    content.className = 'max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-16rem)] overflow-y-auto';

    // Close button — floats just outside the content's top-right corner
    // (up and to the right, clear of the tab bar), same icon/style as the
    // toolbar's save button (a tick: changes here are never discarded).
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.title = 'Tanca';
    closeBtn.textContent = '✓';
    closeBtn.style.cssText = `
      position: absolute;
      top: -3px;
      right: -3px;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      border-radius: 50%;
      border: 1px solid var(--accent);
      background: var(--accent);
      color: #0d1117;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
      z-index: 1;
      font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
    `;
    closeBtn.addEventListener('click', () => this.close());
    wrapper.appendChild(closeBtn);

    this.bodyContainer = document.createElement('div');
    content.appendChild(this.bodyContainer);
    wrapper.appendChild(content);

    this.modalElement.appendChild(wrapper);

    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.close();
    });

    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.escapeHandler);

    document.body.appendChild(this.modalElement);

    return this.bodyContainer;
  }

  /**
   * Temporarily hide the popup without closing it — used while picking a
   * node for a transformation parameter (the "◎" button next to a
   * parameter field): the popup covers the whole screen, so the graph
   * behind it isn't clickable at all otherwise. Also detaches the escape
   * handler so pressing Esc to cancel the pick (GraphVisualizer's own
   * handler) doesn't also close the popup out from under it; show()
   * restores both.
   */
  hide(): void {
    if (!this.modalElement) return;
    this.modalElement.style.display = 'none';
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
  }

  /** Undo hide() — see its doc comment. */
  show(): void {
    if (!this.modalElement) return;
    this.modalElement.style.display = '';
    if (this.escapeHandler) {
      document.addEventListener('keydown', this.escapeHandler);
    }
  }

  close(): void {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }
    if (this.modalElement && this.modalElement.parentNode) {
      this.modalElement.parentNode.removeChild(this.modalElement);
    }
    this.modalElement = null;
    this.bodyContainer = null;

    const cb = this.onCloseCallback;
    this.onCloseCallback = null;
    cb?.();
  }
}
